import { useState } from "react";
import { Box, Text, VStack } from "@chakra-ui/react";
import { Field, FormInput, FormSelect, FormSwitch, FormTextarea } from "@/components/jobOffer/ui";
import { SectionCard, SectionHeader, Grid2 } from "@/components/jobOffer/ui/layout";
import { AddButton, ItemHeader, RepeatableText } from "@/components/jobOffer/ui/listHelpers";
import { sumMaxScore } from "@/scoringUi";
import CampaignInheritedCriteriaSection from "@/components/campaign/CampaignInheritedCriteriaSection";
import ScoringModelSummary from "@/components/jobOffer/ScoringModelSummary";

const ANSWER_TYPE_OPTIONS = [
    { value: "Text", label: "Tekst" },
    { value: "Number", label: "Liczba" },
    { value: "Date", label: "Data" },
    { value: "YesNo", label: "Tak / Nie" },
    { value: "SingleChoice", label: "Jedna odpowiedź" },
    { value: "MultiChoice", label: "Wiele odpowiedzi" },
    { value: "Email", label: "E-mail" },
    { value: "Phone", label: "Telefon" },
    { value: "Address", label: "Adres" },
];

const CHOICE_ANSWER_TYPES = ["SingleChoice", "MultiChoice"];

const DISQUALIFICATION_ACTION_OPTIONS = [
    { value: "EndProcess", label: "Zakończ proces (brak dalszego kontaktu)" },
    { value: "ProposeAlternative", label: "Zaproponuj alternatywną usługę" },
    { value: "LeaveForLater", label: "Zostaw kontakt do późniejszej obsługi" },
    { value: "HandoffToOtherCampaign", label: "Przekaż do innej kampanii" },
];

const newQuestion = () => ({
    id: crypto.randomUUID(), question: "", answerType: "Text", isRequired: false, isCritical: false,
    options: [], conditionQuestionId: null, conditionValue: "",
});

const StepQualifying = ({ values, onChange, campaignId, scenarioId }) => {
    const [scenarioModel, setScenarioModel] = useState(null);
    const scenarioMaxScore = scenarioModel?.criteria
        ? sumMaxScore(scenarioModel.criteria.map(c => ({ scoring: c.scoring })))
        : 0;

    const set = (key, val) => onChange({ ...values, [key]: val });

    const questions = values.qualifyingQuestions ?? [];
    const setQuestions = (next) => set("qualifyingQuestions", next);
    const updateQuestion = (i, patch) => setQuestions(questions.map((q, idx) => idx === i ? { ...q, ...patch } : q));
    const removeQuestion = (i) => setQuestions(questions.filter((_, idx) => idx !== i));
    const addQuestion = () => setQuestions([...questions, newQuestion()]);

    return (
        <VStack w="full" align="stretch" gap={4}>
            <SectionCard>
                <SectionHeader number={6} title="Pytania kwalifikujące" subtitle="Pytania, które Agent ma zebrać podczas rozmowy" />
                <VStack align="stretch" gap={4}>
                    {questions.map((q, i) => {
                        const priorQuestions = questions.slice(0, i);
                        const conditionOptions = [
                            { value: "", label: "Brak warunku" },
                            ...priorQuestions.map((p, idx) => ({ value: p.id, label: p.question || `Pytanie ${idx + 1}` })),
                        ];
                        const isChoiceType = CHOICE_ANSWER_TYPES.includes(q.answerType);

                        return (
                            <Box key={q.id ?? i} borderWidth={1} borderColor="border.primary" borderRadius={8} p={4}>
                                <ItemHeader title={q.question || "Nowe pytanie"} index={i} onRemove={() => removeQuestion(i)} />
                                <VStack align="stretch" gap={3}>
                                    <Field label="Treść pytania" required>
                                        <FormInput value={q.question} onChange={(e) => updateQuestion(i, { question: e.target.value })} />
                                    </Field>
                                    <Field label="Typ odpowiedzi">
                                        <FormSelect value={q.answerType} onChange={(v) => updateQuestion(i, { answerType: v })} options={ANSWER_TYPE_OPTIONS} placeholder="Wybierz typ" />
                                    </Field>
                                    {isChoiceType && (
                                        <RepeatableText
                                            label="Możliwe odpowiedzi"
                                            values={q.options ?? []}
                                            onChange={(v) => updateQuestion(i, { options: v })}
                                            placeholder="Odpowiedź"
                                        />
                                    )}
                                    {priorQuestions.length > 0 && (
                                        <Field label="Warunek zadania pytania" hint="Pokaż tylko jeśli odpowiedź na wcześniejsze pytanie =">
                                            <FormSelect
                                                value={q.conditionQuestionId ?? ""}
                                                onChange={(v) => updateQuestion(i, v ? { conditionQuestionId: v } : { conditionQuestionId: null, conditionValue: "" })}
                                                options={conditionOptions}
                                                placeholder="Brak warunku"
                                            />
                                        </Field>
                                    )}
                                    {q.conditionQuestionId && (
                                        <Field label="Oczekiwana wartość odpowiedzi" hint='np. "TAK"'>
                                            <FormInput value={q.conditionValue ?? ""} onChange={(e) => updateQuestion(i, { conditionValue: e.target.value })} />
                                        </Field>
                                    )}
                                    <Grid2>
                                        <FormSwitch label="Wymagane" checked={!!q.isRequired} onChange={(v) => updateQuestion(i, { isRequired: v })} />
                                        <FormSwitch label="Krytyczne (eliminacyjne)" checked={!!q.isCritical} onChange={(v) => updateQuestion(i, { isCritical: v })} />
                                    </Grid2>
                                </VStack>
                            </Box>
                        );
                    })}
                    <AddButton label="Dodaj pytanie kwalifikujące" onClick={addQuestion} />
                </VStack>
            </SectionCard>

            <SectionCard>
                <SectionHeader number={7} title="Kryteria kwalifikacji (QUALIFIED)" subtitle="Kiedy lead jest uznawany za zakwalifikowanego" />
                <RepeatableText
                    label="Kryteria"
                    values={values.qualificationCriteria ?? []}
                    onChange={(v) => set("qualificationCriteria", v)}
                    placeholder='np. "budżet > 20 000 PLN"'
                />
            </SectionCard>

            <SectionCard>
                <SectionHeader number={8} title="Akcja przy dyskwalifikacji (DISQUALIFIED)" subtitle="Co Agent ma zrobić, gdy lead nie spełnia kryteriów" />
                <VStack align="stretch" gap={4}>
                    <Field label="Akcja">
                        <FormSelect
                            value={values.disqualificationAction ?? ""}
                            onChange={(v) => set("disqualificationAction", v || null)}
                            options={DISQUALIFICATION_ACTION_OPTIONS}
                            placeholder="Wybierz akcję"
                        />
                    </Field>
                    {values.disqualificationAction && (
                        <Field label="Szczegóły" hint="np. nazwa alternatywnej usługi lub kampanii docelowej">
                            <FormTextarea value={values.disqualificationActionDetail ?? ""} onChange={(e) => set("disqualificationActionDetail", e.target.value)} rows={2} />
                        </Field>
                    )}
                </VStack>
            </SectionCard>

            <SectionCard>
                <SectionHeader number={9} title="Kryteria odziedziczone ze scenariusza" subtitle="Kryteria zdefiniowane na poziomie scenariusza, z możliwością nadpisania dla tej kampanii" />
                <VStack align="stretch" gap={4}>
                    <ScoringModelSummary localCriteria={[]} scenarioMaxScore={scenarioMaxScore} />
                    <CampaignInheritedCriteriaSection
                        campaignId={campaignId}
                        scenarioId={scenarioId}
                        scoringOverrides={values.scoringOverrides ?? []}
                        onOverridesChange={(next) => onChange({ ...values, scoringOverrides: next })}
                        onModelLoaded={setScenarioModel}
                    />
                    {!scenarioModel?.criteria?.length && (
                        <Text color="text.grey" fontSize="sm">Ten scenariusz nie ma jeszcze zdefiniowanych kryteriów kwalifikujących.</Text>
                    )}
                </VStack>
            </SectionCard>
        </VStack>
    );
};

export default StepQualifying;
